import React from 'react';
import { useSelector } from 'react-redux';
import { CircularProgress, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import InlineBanner from '../components/InlineBanner';
import SummaryCard, { SummaryDetail } from '../components/SummaryCard';
import { selectWaActive, WaRootState } from './selectors';
import useWaErrorText from './errorText';
import { WaTemplateItem } from '../../../Models/DataSources/SmartSendWhatsApp';

// SmartSend part B — the WhatsApp card of the send-summary dialog (a SummaryCard), plus the
// confirmation line (`WaConfirmLine`), which sits under the cards.
//
// 🔴 THE COST IS NOT ON THIS SCREEN. §2.4 took it off, and the owner settled the replacement on
// 2026-09-13: the cost-confirmation CHECKBOX is gone and the confirmation line stays, WITHOUT
// the amount and WITH the numbers — "יישלח עכשיו: N מיילים ו-M הודעות וואטסאפ". The contract's
// §7 still lists `summary.costCheckbox` and §6 still requires the control; both are superseded.
// See `C:\BitBucketProjects\PulseemReaxt\LEDGER-C-CLIENT.md` (beside the artefacts, per
// `_deploy\19-PARALLEL-BUILD.md §2` — it is not in the documentation tree).
//
// ⚠️ The one ₪ still on this screen is inside the SAMPLE MESSAGE — it is Clal's campaign copy
// ("נותרו לך ₪37,100 למדרגה הבאה"), not a price. It lives in the phone preview and must not be
// touched by anything here.
//
// Balance and tier are WARNINGS, not blockers (contract §6): `Send` stays live. The server's
// pre-gate answers 2/3 with "carry on" for exactly this reason — the operator may knowingly
// send the part that fits. They stay outside the details.
//
// Counters (DataSources_WhatsApp_Summary_Stage): all are SOURCE ROWS except Queued (distinct phones =
// messages) and PendingQueued (a subset of it). TotalRows = Queued + the three Skipped buckets.

const useStyles = makeStyles((theme) => ({
    confirm: { marginTop: theme.spacing(2), fontWeight: 600 },
}));

interface Props {
    /** "פרטים נוספים" state, owned by the dialog. */
    expanded: boolean;
    onToggle: () => void;
    /** `null` ⇒ no unit to preview. */
    onPreview: (() => void) | null;
    previewDisabled?: boolean;
}

const WaSummaryBlock: React.FC<Props> = ({ expanded, onToggle, onPreview, previewDisabled }) => {
    const { t } = useTranslation();
    const { waErrorText } = useWaErrorText();

    const active = useSelector((s: WaRootState) => selectWaActive(s));
    const summary = useSelector((s: any) => s.smartSendWa.waSummary);
    const status = useSelector((s: any) => s.smartSendWa.summaryStatus);
    const error = useSelector((s: any) => s.smartSendWa.summaryError);
    // Details from the store only; the template named as the picker names it.
    const templates: WaTemplateItem[] = useSelector((s: any) => s.smartSendWa.waTemplates);
    const templateId: string | null = useSelector((s: any) => s.smartSendWa.whatsAppTemplateId);
    const fromNumber: string | null = useSelector(
        (s: any) => s.smartSendWa.whatsAppFromNumber ?? s.smartSendWa.waMapping?.FromNumber ?? null);

    if (!active) return null;

    const tpl = templateId ? templates.find((tp) => tp.TemplateId === templateId) : undefined;
    const templateName: string = (tpl && (tpl.FriendlyTemplateName || tpl.TemplateName)) || summary?.TemplateName || '';

    const ready = status === 'succeeded' && !!summary;
    const n = (v: any): number => (typeof v === 'number' && Number.isFinite(v) ? v : 0);
    const queued = n(summary?.Queued);

    const details: SummaryDetail[] = [];
    const add = (key: string, label: string, value: React.ReactNode) => details.push({ key, label, value });
    const addCount = (key: string, label: string, value: number) => { if (value > 0) add(key, label, value.toLocaleString()); };

    if (templateName) add('template', t('DataSources.send.whatsapp.mapping.templateLabel'), templateName);
    if (fromNumber) add('from', t('DataSources.send.whatsapp.picker.fromNumber'), fromNumber);

    let shortfall: string | null = null;
    if (ready) {
        const noPhone = n(summary.SkippedNoPhone);
        const dupes = n(summary.SkippedDuplicates);
        const optedOut = n(summary.SkippedOptedOut);
        const skipped = noPhone + dupes + optedOut;
        const totalRows = typeof summary.TotalRows === 'number' ? summary.TotalRows : queued + skipped;
        if (skipped > 0) {
            shortfall = t('DataSources.send.whatsapp.summary.rowsNotSent', {
                count: skipped.toLocaleString(), total: totalRows.toLocaleString(),
            });
        }
        addCount('totalRows', t('DataSources.send.whatsapp.summary.totalRows'), totalRows);
        addCount('optedOut', t('DataSources.send.whatsapp.summary.optedOutRows'), optedOut);
        addCount('noPhone', t('DataSources.send.whatsapp.summary.noPhoneRows'), noPhone);
        addCount('dupes', t('DataSources.send.whatsapp.summary.duplicatePhoneRows'), dupes);
        // `SmsStatus = 5` recipients ARE sent (v1.3) — an informational subset of `Queued`, never a
        // deduction from it.
        addCount('pending', t('DataSources.send.whatsapp.summary.pendingOfQueued'), n(summary.PendingQueued));
        addCount('both', t('DataSources.send.whatsapp.summary.rowsBoth'), n(summary.BothIdentities));
        addCount('emailOnly', t('DataSources.send.whatsapp.summary.rowsEmailOnly'), n(summary.EmailOnly));
        addCount('phoneOnly', t('DataSources.send.whatsapp.summary.rowsPhoneOnly'), n(summary.PhoneOnly));
        addCount('noIdentity', t('DataSources.send.whatsapp.summary.rowsNoIdentity'), n(summary.NoIdentity));
    }

    return (
        <SummaryCard
            id="wa"
            title={t('DataSources.send.whatsapp.summary.title')}
            onPreview={onPreview}
            previewDisabled={previewDisabled}
            count={ready ? queued : null}
            countLabel={t('DataSources.send.whatsapp.summary.queued')}
            status={(
                <>
                    {status === 'loading' && <CircularProgress size={18} style={{ marginTop: 12 }} />}
                    {/* A 4xx from `POST WhatsApp/Summary` carries a §4 `Message`. It is rendered
                        through `error.<Message>` and NOT as the raw server string: this dialog has
                        never known about 409. Send is disabled by the parent on the same condition —
                        an e-mail-only send in silence is precisely what must not happen here. */}
                    {status === 'failed' && (
                        <div style={{ marginTop: 12 }}>
                            <InlineBanner severity="error" role="alert" size="lg" title={waErrorText(error)} />
                        </div>
                    )}
                </>
            )}
            shortfall={shortfall}
            details={details}
            expanded={expanded}
            onToggle={onToggle}
        >
            {/* WARNINGS, not blockers. */}
            {ready && summary.HasBalance === false && (
                <InlineBanner
                    severity="warning"
                    role="status"
                    title={t('DataSources.send.whatsapp.summary.noBalance', { n: queued.toLocaleString() })}
                />
            )}
            {ready && summary.WhatsappSmsLeft != null && summary.WhatsappSmsLeft < queued && (
                <InlineBanner
                    severity="warning"
                    role="status"
                    title={t('DataSources.send.whatsapp.summary.tierLeft', { n: (summary.WhatsappSmsLeft ?? 0).toLocaleString() })}
                />
            )}
        </SummaryCard>
    );
};

// 🔴 THE CONFIRMATION LINE — no amount, with the numbers (owner, 2026-09-13). It replaces the cost
// checkbox outright: there is no control to tick any more, so a send can no longer be blocked by a
// confirmation about money the screen does not show. It is rendered whenever a leg exists, including
// `Queued = 0` — the operator is then told plainly that zero WhatsApp messages will go out.
export const WaConfirmLine: React.FC<{ emailRecipients: number }> = ({ emailRecipients }) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const active = useSelector((s: WaRootState) => selectWaActive(s));
    const hasLeg = useSelector((s: any) => !!s.smartSendWa.waMapping?.HasLeg);
    const queued = useSelector((s: any) => s.smartSendWa.waSummary?.Queued ?? 0);
    if (!active || !hasLeg) return null;
    return (
        <Typography variant="body1" className={classes.confirm}>
            {t('DataSources.send.whatsapp.summary.confirmLine', {
                email: emailRecipients.toLocaleString(),
                wa: queued.toLocaleString(),
            })}
        </Typography>
    );
};

export default WaSummaryBlock;
